import { useState } from "react";

/**
 * 批量操作栏（选中 ≥1 行时出现）。
 *
 * 安全设计：这里只负责「收集意图与参数」，真正的执行必须经过 ConfirmDialog 的二次确认
 * （影响预览 + type-to-confirm）。也就是说，批量改动**不存在**单击即生效的路径。
 */

export interface BulkActionBarProps {
  readonly selectedCount: number;
  readonly accountCount: number;
  readonly busy: boolean;
  readonly onPause: () => void;
  readonly onResume: () => void;
  readonly onBudget: (dailyBudgetCents: number) => void;
  readonly onClear: () => void;
  /** 选中的行是否都支持预算调整（广告层不支持）。false 时禁用预算输入并显示原因。 */
  readonly budgetEnabled?: boolean;
  /** 预算被禁用时的说明（例如「选中项含广告层实体」）。 */
  readonly budgetHint?: string | null;
  /** 展示币种（只影响标签文案）。 */
  readonly currency?: string;
}

export function BulkActionBar({
  selectedCount,
  accountCount,
  busy,
  onPause,
  onResume,
  onBudget,
  onClear,
  budgetEnabled = true,
  budgetHint = null,
  currency = "USD",
}: BulkActionBarProps) {
  const [budgetInput, setBudgetInput] = useState("50");

  const parsed = Number.parseFloat(budgetInput);
  const budgetCents = Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : null;

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-line bg-raised px-3 py-2 elev-lg">
      <span className="text-table font-medium text-ink tnum">
        已选 {selectedCount} 条 · 涉及 {accountCount} 个账户
      </span>

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onResume}
          className="rounded-control border border-line px-2 text-meta text-ink transition-colors dur-fast hover:bg-hover disabled:opacity-50"
          style={{ height: "var(--control-height)" }}
        >
          恢复投放
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onPause}
          className="rounded-control border border-danger/50 bg-danger-subtle px-2 text-meta font-medium text-danger-text transition-opacity dur-fast hover:opacity-90 disabled:opacity-50"
          style={{ height: "var(--control-height)" }}
        >
          批量暂停
        </button>

        <div className="flex items-center gap-1 rounded-control border border-line px-2" style={{ height: "var(--control-height)" }}>
          <label htmlFor="bulk-budget" className="text-2xs text-ink-3">
            日预算 {currency}
          </label>
          <input
            id="bulk-budget"
            value={budgetInput}
            inputMode="decimal"
            disabled={!budgetEnabled}
            onChange={(event) => setBudgetInput(event.target.value)}
            className="w-16 bg-transparent text-right text-table text-ink tnum outline-none disabled:opacity-40"
          />
          <button
            type="button"
            disabled={busy || !budgetEnabled || budgetCents === null}
            onClick={() => {
              if (budgetCents !== null) onBudget(budgetCents);
            }}
            className="rounded-control bg-accent px-2 text-meta font-medium text-accent-ink transition-colors dur-fast hover:bg-accent-hover disabled:opacity-50"
          >
            应用
          </button>
          {budgetEnabled ? null : <span className="text-2xs text-ink-3">{budgetHint ?? "选中的行不支持改预算"}</span>}
        </div>

        <button
          type="button"
          onClick={onClear}
          className="rounded-control border border-line px-2 text-meta text-ink-2 transition-colors dur-fast hover:bg-hover"
          style={{ height: "var(--control-height)" }}
        >
          清除选择
        </button>
      </div>
    </div>
  );
}
